/**
 * recipe.js — Aurum & Ash recipes
 * Two responsibilities:
 *   1. Share / copy-link buttons on recipe detail pages.
 *   2. Client-side filter chip bar on the recipes index.
 */
(function () {
  'use strict';

  var AA = window.AurumAsh = window.AurumAsh || {};
  AA.recipe = AA.recipe || {};

  // ------------------------------------------------------------------
  // Sharing
  // ------------------------------------------------------------------
  function showToast(message) {
    var toast = document.createElement('div');
    toast.className = 'aa-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('aa-toast--in'); });
    setTimeout(function () {
      toast.classList.remove('aa-toast--in');
      setTimeout(function () { toast.remove(); }, 300);
    }, 1800);
  }

  function copyLink() {
    var url = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function () {
        showToast('Link copied');
      }).catch(function () {
        showToast('Could not copy link');
      });
    } else {
      var input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      try { document.execCommand('copy'); showToast('Link copied'); }
      catch (e) { showToast('Could not copy link'); }
      input.remove();
    }
  }

  function nativeShare() {
    var title = document.title;
    var text = (document.querySelector('meta[name="description"]') || {}).content || '';
    var url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: title, text: text, url: url }).catch(function () {
        // User cancelled — silent.
      });
    } else {
      copyLink();
    }
  }

  function initShare() {
    var native = document.querySelector('[data-recipe-share-native]');
    var copy = document.querySelector('[data-recipe-share-copy]');
    if (native) native.addEventListener('click', nativeShare);
    if (copy) copy.addEventListener('click', copyLink);
  }

  // ------------------------------------------------------------------
  // Index filters
  // ------------------------------------------------------------------
  function initFilters() {
    var filtersEl = document.querySelector('[data-recipes-filters]');
    if (!filtersEl) return;
    var resultsEl = document.querySelector('[data-recipes-results]');
    var emptyEl = document.querySelector('[data-recipes-empty]');
    var featuredEl = document.querySelector('[data-recipes-featured]');
    var featuredHandle = featuredEl ? (featuredEl.dataset.featuredHandle || '') : '';
    var toggleBtn = filtersEl.querySelector('[data-recipes-filter-toggle]');
    var countEl = filtersEl.querySelector('[data-recipes-filter-count]');
    if (!resultsEl) return;

    // Active filter state, one value per group, '' = all.
    var state = { type: '', 'spirit-category': '', method: '', difficulty: '', saved: '' };

    function isAnyFilterActive() {
      return !!(state.type || state['spirit-category'] || state.method || state.difficulty || state.saved);
    }

    function applyFilters() {
      var anyActive = isAnyFilterActive();

      // Hide the featured block when any filter is active; show otherwise.
      if (featuredEl) featuredEl.hidden = anyActive;

      var cards = resultsEl.querySelectorAll('.aa-recipe-card');
      var anyVisible = false;
      cards.forEach(function (card) {
        var match = true;
        if (state.type && card.dataset.type !== state.type) match = false;
        if (state['spirit-category'] && card.dataset.spiritCategory !== state['spirit-category']) match = false;
        if (state.method && card.dataset.method !== state.method) match = false;
        if (state.difficulty && card.dataset.difficulty !== state.difficulty) match = false;
        if (state.saved === 'saved') {
          var handle = card.dataset.recipeHandle;
          if (!handle || !window.AurumAsh || !window.AurumAsh.engagement || !window.AurumAsh.engagement.isSaved(handle)) {
            match = false;
          }
        }
        // When no filter is active, hide the in-grid duplicate of today's featured.
        if (!anyActive && featuredHandle && card.dataset.recipeHandle === featuredHandle) {
          match = false;
        }
        card.style.display = match ? '' : 'none';
        if (match) anyVisible = true;
      });

      // Hide empty section headings.
      var sections = resultsEl.querySelectorAll('.aa-recipes-index__section');
      sections.forEach(function (sec) {
        var visible = sec.querySelectorAll('.aa-recipe-card:not([style*="display: none"])').length > 0;
        sec.style.display = visible ? '' : 'none';
      });

      if (emptyEl) emptyEl.hidden = anyVisible;

      // Sync URL hash for shareability.
      var parts = [];
      Object.keys(state).forEach(function (k) {
        if (state[k]) parts.push(k + '=' + state[k]);
      });
      var hash = parts.length ? '#' + parts.join('&') : window.location.pathname;
      history.replaceState(null, '', hash);

      updateFilterCount();
    }

    // Reflect the number of active filter groups on the mobile toggle.
    function updateFilterCount() {
      if (!countEl) return;
      var n = 0;
      Object.keys(state).forEach(function (k) { if (state[k]) n += 1; });
      countEl.textContent = n ? String(n) : '';
      countEl.hidden = n === 0;
    }

    function readHash() {
      var h = window.location.hash.replace(/^#/, '');
      if (!h) return;
      h.split('&').forEach(function (pair) {
        var kv = pair.split('=');
        if (state.hasOwnProperty(kv[0])) state[kv[0]] = kv[1] || '';
      });
      // Mirror state into chip active states.
      Object.keys(state).forEach(function (group) {
        var groupEl = filtersEl.querySelector('[data-filter-group="' + group + '"]');
        if (!groupEl) return;
        groupEl.querySelectorAll('.aa-chip').forEach(function (chip) {
          chip.classList.toggle('aa-chip--active', chip.dataset.filterValue === state[group]);
        });
      });
    }

    filtersEl.addEventListener('click', function (e) {
      var chip = e.target.closest('.aa-chip');
      if (!chip) return;
      var groupEl = chip.closest('[data-filter-group]');
      if (!groupEl) return;
      var group = groupEl.dataset.filterGroup;
      state[group] = chip.dataset.filterValue;
      groupEl.querySelectorAll('.aa-chip').forEach(function (c) {
        c.classList.toggle('aa-chip--active', c === chip);
      });
      applyFilters();
    });

    // Mobile: collapse/expand the filter panel.
    if (toggleBtn) {
      toggleBtn.addEventListener('click', function () {
        var open = filtersEl.classList.toggle('aa-recipes-index__filters--open');
        toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    readHash();
    applyFilters();

    if (window.AurumAsh && window.AurumAsh.engagement) {
      window.AurumAsh.engagement.on('change', function () {
        if (state.saved === 'saved') applyFilters();
      });
    }
  }

  // ------------------------------------------------------------------
  function init() { initShare(); initFilters(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  AA.recipe.copyLink = copyLink;
  AA.recipe.nativeShare = nativeShare;
})();
